/**
 * Schémas spécifiques aux opérations super-administrateur.
 * Création / édition de comptes pour les 4 rôles. Discriminated union par rôle
 * pour valider précisément chaque cas.
 */
import { z } from 'zod';
import { emailSchema, passwordSchema } from './common.js';
// ─── Création ────────────────────────────────────────────────────────────
const baseCreate = {
    email: emailSchema,
    password: passwordSchema,
};
export const adminCreateStudentSchema = z.object({
    ...baseCreate,
    role: z.literal('student'),
    civilite: z.enum(['M', 'Mme', 'X']).optional().or(z.literal('')),
    prenom: z.string().trim().min(1).max(100),
    nom: z.string().trim().min(1).max(100),
    // Optionnel : si absent, le backend rattache à HORS-FORMATION par défaut.
    formationCode: z.string().trim().min(1).optional().or(z.literal('')).transform((v) => v || undefined),
    academicYear: z.string().trim().regex(/^\d{4}-\d{4}$/).optional(),
    // V7 (mai 2026) — rattachement optionnel à un établissement.
    establishmentId: z.string().uuid().optional().or(z.literal('')).transform((v) => v || undefined),
});
export const adminCreateCompanySchema = z.object({
    ...baseCreate,
    role: z.literal('company'),
    raisonSociale: z.string().trim().min(2).max(200),
    contactPrenom: z.string().trim().min(1).max(100),
    contactNom: z.string().trim().min(1).max(100),
    contactFonction: z.string().trim().min(1).max(150),
    // Optionnel : non obligatoire à la création, peut être complété par l'entreprise.
    contactTelephone: z
        .string()
        .trim()
        .regex(/^\+?[\d\s().-]{6,20}$/, 'Téléphone invalide.')
        .optional()
        .or(z.literal(''))
        .transform((v) => v || undefined),
    ville: z.string().trim().max(100).optional().or(z.literal('')),
    // Création par admin → directement validé par défaut
    validationStatus: z.enum(['pending', 'validated', 'rejected']).default('validated'),
});
export const adminCreateResponsableSchema = z.object({
    ...baseCreate,
    role: z.literal('responsable'),
    prenom: z.string().trim().min(1).max(100),
    nom: z.string().trim().min(1).max(100),
    fonction: z.string().trim().max(150).optional().or(z.literal('')),
    // V7 (mai 2026) — un responsable est rattaché à UN établissement.
    // Le super-admin peut modifier ce rattachement à tout moment via
    // PATCH /admin/users/:id/scope.
    establishmentId: z.string().uuid('Établissement requis.'),
    // V7 (mai 2026) — granularité fine : le responsable gère soit toutes
    // les formations de son établissement, soit un sous-ensemble explicite.
    // Une liste vide = "toutes les formations de l'établissement".
    formationIds: z.array(z.string().uuid()).default([]),
    // Rétrocompatibilité : l'ancienne UI peut encore envoyer formationCodes.
    formationCodes: z.array(z.string().trim().min(1)).optional(),
});
export const adminCreateSuperAdminSchema = z.object({
    ...baseCreate,
    role: z.literal('super_admin'),
});
export const adminCreateUserSchema = z.discriminatedUnion('role', [
    adminCreateStudentSchema,
    adminCreateCompanySchema,
    adminCreateResponsableSchema,
    adminCreateSuperAdminSchema,
]);
// ─── Édition ─────────────────────────────────────────────────────────────
// Pour la mise à jour, on n'autorise pas le changement de rôle (changer un
// étudiant en entreprise n'a pas de sens et ouvrirait des trous data). On
// garde un schéma par rôle pour les champs modifiables.
export const adminUpdateStudentSchema = z.object({
    email: emailSchema.optional(),
    civilite: z.enum(['M', 'Mme', 'X']).optional().or(z.literal('')),
    prenom: z.string().trim().min(1).max(100).optional(),
    nom: z.string().trim().min(1).max(100).optional(),
    telephone: z
        .string()
        .trim()
        .regex(/^\+?[\d\s().-]{6,20}$/)
        .optional()
        .or(z.literal('')),
    pitch: z.string().max(800).optional().or(z.literal('')),
    // V1.0 (mai 2026) : statuts étendus + alias legacy fulfilled.
    status: z
        .enum(['draft', 'published', 'paused', 'placed', 'archived', 'fulfilled'])
        .optional(),
    // V1.1 (mai 2026) — réaffectation de formation / promotion par l'admin.
    // formationCode seul suffit si on veut changer de formation en gardant
    // la même année académique (résolution côté serveur). Les deux ensemble
    // permettent un changement complet.
    formationCode: z.string().trim().min(1).max(30).optional(),
    academicYear: z.string().trim().regex(/^\d{4}-\d{4}$/, 'Format attendu : YYYY-YYYY').optional(),
});
export const adminUpdateCompanySchema = z.object({
    email: emailSchema.optional(),
    raisonSociale: z.string().trim().min(2).max(200).optional(),
    contactPrenom: z.string().trim().min(1).max(100).optional(),
    contactNom: z.string().trim().min(1).max(100).optional(),
    contactFonction: z.string().trim().min(1).max(150).optional(),
    contactTelephone: z
        .string()
        .trim()
        .regex(/^\+?[\d\s().-]{6,20}$/)
        .optional()
        .or(z.literal('')),
    siret: z.string().trim().regex(/^\d{14}$/).optional().or(z.literal('')),
    ville: z.string().trim().max(100).optional().or(z.literal('')),
    description: z.string().max(2000).optional().or(z.literal('')),
    validationStatus: z.enum(['pending', 'validated', 'rejected']).optional(),
});
export const adminUpdateResponsableSchema = z.object({
    email: emailSchema.optional(),
    prenom: z.string().trim().min(1).max(100).optional(),
    nom: z.string().trim().min(1).max(100).optional(),
    fonction: z.string().trim().max(150).optional().or(z.literal('')),
    // V7 (mai 2026) — modification du rattachement.
    establishmentId: z.string().uuid().optional().or(z.null()),
    // Granularité fine : sous-ensemble de formations OU toutes celles de
    // l'établissement (tableau vide).
    formationIds: z.array(z.string().uuid()).optional(),
    // Rétrocompatibilité.
    formationCodes: z.array(z.string().trim().min(1)).optional(),
});
// ─── V7 (mai 2026) — Settings plateforme ─────────────────────────────────
//
// Singleton. Pilote la visibilité par défaut des annonces et les
// conditions d'inscription/profil.
export const platformSettingsSchema = z.object({
    allowSelfRegisteredCompanies: z.boolean(),
    defaultJobOfferVisibility: z.enum([
        'public',
        'restricted_formations',
        'restricted_establishment',
    ]),
    forceSelfCompaniesPublic: z.boolean(),
    requireEstablishmentForStudentsInTraining: z.boolean(),
});
export const adminUpdateSuperAdminSchema = z.object({
    email: emailSchema.optional(),
});
// ─── Établissements (Vague A) ────────────────────────────────────────────
//
// Admin uniquement. Permet de créer / modifier les lycées, CFA, écoles
// supérieures, universités et organismes de formation. Chaque candidat
// scolaire est rattaché à une formation, qui appartient à un établissement.
export const ESTABLISHMENT_TYPES = [
    'lycee_pro',
    'lycee_general',
    'cfa',
    'ecole_superieure',
    'universite',
    'organisme_formation',
    'autre',
];
export const ESTABLISHMENT_TYPE_LABELS = {
    lycee_pro: 'Lycée professionnel',
    lycee_general: 'Lycée général',
    cfa: 'CFA',
    ecole_superieure: 'École supérieure',
    universite: 'Université',
    organisme_formation: 'Organisme de formation',
    autre: 'Autre',
};
export const establishmentTypeSchema = z.enum(ESTABLISHMENT_TYPES);
export const adminEstablishmentCreateSchema = z.object({
    slug: z
        .string()
        .trim()
        .min(2)
        .max(100)
        .regex(/^[a-z0-9-]+$/, 'Identifiant : minuscules, chiffres, tirets uniquement.'),
    name: z.string().trim().min(2).max(200),
    type: establishmentTypeSchema,
    city: z.string().trim().max(100).optional().or(z.literal('')),
    region: z.string().trim().max(100).optional().or(z.literal('')),
    website: z.string().trim().url().max(500).optional().or(z.literal('')),
    contactEmail: emailSchema.optional().or(z.literal('')),
    contactPhone: z.string().trim().max(30).optional().or(z.literal('')),
    active: z.boolean().default(true),
});
export const adminEstablishmentUpdateSchema = adminEstablishmentCreateSchema.partial();
// ─── Définition manuelle d'un mot de passe ───────────────────────────────
// Le super-admin peut imposer un mot de passe précis (utile en assistance
// téléphonique pour un candidat ou un employeur en difficulté). Le mot de
// passe doit respecter les mêmes règles que tous les autres mots de passe
// de l'application : passwordSchema (12 chars min, complexité, etc.).
export const adminSetPasswordSchema = z.object({
    password: passwordSchema,
    notifyEmail: z.boolean().optional().default(false),
});
//# sourceMappingURL=admin.js.map